import { BenefitActivityLogItemType } from "../../../../../../../redux/slices/partner-program/benefits-log/Types";
import { BenefitLogFile, BenefitLogHistoryRow, BenefitLogStatus } from "./mockData";
import { getPlainTextLength } from "../../../../../../../common/utils/richText";

export { getPlainTextLength };

export const formatDisplayDate = (date: Date | string | null | undefined): string => {
  if (!date) return "-";
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  const day = parsed.getDate();
  const month = parsed.toLocaleDateString("en-GB", { month: "short" });
  const year = parsed.getFullYear();
  return `${day} ${month}, ${year}`;
};

export const formatFileSizeLabel = (sizeBytes?: number | null): string => {
  if (!sizeBytes || sizeBytes <= 0) return "-";
  const sizeKb = Math.max(1, Math.round(sizeBytes / 1024));
  return `${sizeKb}KB`;
};

export const formatUploadedBy = (
  uploadedBy: number | string | null | undefined
): string => {
  const value =
    uploadedBy !== null && uploadedBy !== undefined
      ? String(uploadedBy).trim()
      : "";
  return value || "-";
};

export const formatOwnerNames = (
  users?: Array<{ full_name?: string; first_name?: string; last_name?: string }>
): string => {
  if (!users?.length) return "-";
  return users
    .map(
      (user) =>
        user.full_name ||
        `${user.first_name || ""} ${user.last_name || ""}`.trim()
    )
    .filter(Boolean)
    .join(", ");
};

export const mapActivityLogToHistoryRow = (
  log: BenefitActivityLogItemType
): BenefitLogHistoryRow => {
  const files: BenefitLogFile[] = (log.media || []).map((file) => ({
    id: String(file.ID),
    name: file.file_name || "-",
    sizeLabel: formatFileSizeLabel(file.size_bytes),
    uploadDate: formatDisplayDate(file.upload_date),
    uploadedBy: formatUploadedBy(file.uploaded_by),
    filePath: file.file_path || undefined,
  }));
  const comment = log.comments || "";
  const status = (log.status || "").trim() || "-";

  return {
    id: String(log.ID),
    benefit: log.benefits_name || "-",
    category: log.category_name || "",
    plannedDate: formatDisplayDate(log.planned_date),
    deliveredDate: formatDisplayDate(log.delivered_date),
    status,
    hidOwner: formatOwnerNames(log.HID_user_id),
    partnerOwner: formatOwnerNames(log.partner_user_id),
    comment,
    files,
    hasCommentsOrFiles: Boolean(
      getPlainTextLength(comment) > 0 || files.length > 0
    ),
  };
};

/** Parses table display dates ("15 Mar, 2026") and dd/mm/yyyy strings. */
export const parseHistoryDate = (value: string): Date | null => {
  if (!value || value === "-") return null;
  const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const dayMonYear = value.match(/^(\d{1,2})\s+([A-Za-z]{3,}),?\s+(\d{4})$/);
  if (dayMonYear) {
    const [, day, mon, year] = dayMonYear;
    const parsed = new Date(`${day} ${mon} ${year}`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isDateInRange = (value: string, range: string[]): boolean => {
  if (!range.length) return true;
  const date = parseHistoryDate(value);
  if (!date) return false;
  const from = range[0] ? new Date(range[0]) : null;
  const to = range[1] ? new Date(range[1]) : null;
  if (from) {
    from.setHours(0, 0, 0, 0);
    if (date < from) return false;
  }
  if (to) {
    to.setHours(23, 59, 59, 999);
    if (date > to) return false;
  }
  return true;
};

export const statusClassName = (status: BenefitLogStatus) => {
  const normalized = (status || "").toLowerCase().replace(/\s+/g, " ");
  if (normalized === "delivered") return "delivered";
  if (normalized === "on hold") return "on-hold";
  if (normalized === "ongoing") return "ongoing";
  if (normalized === "overdue") return "overdue";
  if (normalized === "pending") return "pending";
  return "default";
};
